import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { light } from '@fortawesome/fontawesome-svg-core/import.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';
import {
  Form,
  FormControl,
  FormControlProps,
  FormGroup,
} from 'react-bootstrap';
import { Overwrite } from '../../types';
import IconButton from '../IconButton';

export type BaseFormControlProps = Overwrite<
  FormControlProps,
  {
    value?: number | null | undefined;
    onChange?: (value: number | null | undefined) => void;
  }
>;

export type NumberInputProps = BaseFormControlProps & {
  label?: string;
  icon?: IconProp;
  maxValue?: string;
  minValue?: string;
  isPercent?: boolean;
};

const NumberInput: React.FC<NumberInputProps> = ({
  label,
  icon,
  disabled,
  value,
  onChange,
  isValid,
  isInvalid,
  className,
  defaultValue,
  maxValue,
  minValue,
  isPercent,
  ...props
}) => {
  const setIcon = (iconName: IconProp) => (
    <FontAwesomeIcon icon={iconName} className={`icon`} />
  );

  const [localValue, setLocalValue] = React.useState<any>(
    value === null ? '' : value
  );

  const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const matches = event.target.value.match(/(-?\d*(\.[0-9]{0,2})?)/);

    if (!matches) {
      return;
    }

    setLocalValue(matches[0]);
    let newValue = parseFloat(matches[0]);

    if (maxValue || minValue) {
      const max = maxValue || Number.MAX_VALUE;
      const min = minValue || Number.MIN_VALUE;

      const restrictedValue = Math.max(
        +min,
        Math.min(+max, Number(event.target.value))
      );

      newValue = restrictedValue;
      setLocalValue(restrictedValue);
    }

    if (value !== newValue) {
      onChange && onChange(newValue);
    }

    if (isNaN(newValue)) {
      setLocalValue('');
      return onChange && onChange(null);
    }
  };

  React.useEffect(() => {
    setLocalValue(value === null ? '' : value);
  }, [value]);

  const decrementClick = () => {
    let newValue = Number(localValue);

    if (newValue % 1 !== 0) {
      newValue = Math.floor(newValue);
    } else if (minValue) {
      if (newValue > +minValue) {
        newValue = Number(newValue - 1);
      }
    } else {
      newValue = Number(newValue - 1);
    }

    onChange && onChange(newValue);
  };

  const incrementClick = () => {
    let newValue = Number(localValue);

    if (newValue % 1 !== 0) {
      newValue = Math.ceil(newValue);
    } else if (maxValue) {
      if (newValue < +maxValue) {
        newValue = Number(newValue + 1);
      }
    } else {
      newValue = Number(newValue + 1);
    }

    onChange && onChange(newValue);
  };

  const NumberStepper: React.FC<{
    disabled?: boolean;
    isPercent?: boolean;
  }> = ({ disabled, isPercent }) => (
    <div
      className={`number-stepper d-flex flex-column ${
        isPercent ? 'percent' : ''
      }`}
    >
      <IconButton
        icon={light('angle-up')}
        onClick={incrementClick}
        className="p-0"
        disabled={disabled}
      />
      <IconButton
        icon={light('angle-down')}
        onClick={decrementClick}
        className="p-0"
        disabled={disabled}
      />
    </div>
  );

  return (
    <FormGroup className={`${className ? className : ''} number-input-custom`}>
      {label && <Form.Label>{label}</Form.Label>}
      <div className="input-wrapper">
        {icon && setIcon(icon)}
        <FormControl
          type={'number'}
          value={localValue}
          onChange={handleOnChange}
          isValid={isValid}
          isInvalid={isInvalid}
          disabled={disabled}
          {...props}
        />
        <NumberStepper disabled={disabled} isPercent={isPercent} />
        {isPercent && (
          <div className="percent-icon">
            <FontAwesomeIcon icon={light('percent')} className={`rightIcon`} />
          </div>
        )}
      </div>
    </FormGroup>
  );
};

export default NumberInput;
